/**
 * «Мои подписки»: короткая карточка на каждую, подробности — по нажатию.
 *
 * Полная карточка (подключение, ссылка, срок, продление) уже одна занимает
 * экран, поэтому в списке только то, что нужно, чтобы выбрать подписку.
 */
import { useNavigate } from 'react-router';

import { useSubscription } from '@/entities/subscription/queries';
import { describeState } from '@/entities/subscription/state';
import type { SubscriptionItem } from '@/shared/api/schemas';
import { daysWord, formatShortDate } from '@/shared/lib/format';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Card, GroupCaption } from '@/shared/ui/kit/Card';
import { Icon } from '@/shared/ui/kit/Icon';
import { Row } from '@/shared/ui/kit/Row';
import { Status } from '@/shared/ui/kit/Status';

import styles from './Subscriptions.module.css';

function SubscriptionCard({
  item,
  number,
  onOpen,
}: {
  item: SubscriptionItem;
  number: number;
  onOpen: () => void;
}) {
  const view = describeState(item.state);
  const devices = item.device_limit === null ? '∞' : item.device_limit;

  return (
    <button type="button" className={styles.item} onClick={onOpen}>
      <span className={styles.between}>
        <Status tone={view.tone}>{view.label}</Status>
        <span className={styles.dim}>Подписка {number}</span>
      </span>

      <span className={`${styles.between} ${styles.figure}`}>
        {item.state === 'grace' ? (
          <span className={styles.dim}>
            {item.expires_at ? `Закончилась ${formatShortDate(item.expires_at)} · ` : ''}
            доступ ещё открыт
          </span>
        ) : item.has_access && item.days_left !== null ? (
          <span>
            <span className={styles.days}>{item.days_left}</span>
            <span className={`${styles.dim} ${styles.after}`}>
              {daysWord(item.days_left)}
              {item.expires_at ? ` · до ${formatShortDate(item.expires_at)}` : ''}
            </span>
          </span>
        ) : (
          <span className={styles.dim}>
            {item.is_lifetime
              ? 'Бессрочно'
              : item.expires_at
                ? `Закончилась ${formatShortDate(item.expires_at)}`
                : 'Не действует'}
          </span>
        )}
        <span className={styles.chevron}>
          <Icon name="chevron" size={20} />
        </span>
      </span>

      <span className={`${styles.between} ${styles.meta} ${styles.dim}`}>
        <span className={styles.inline}>
          <Icon name="device" size={15} />
          до {devices} устройств
        </span>
        <span>трафик ∞</span>
      </span>
    </button>
  );
}

export function SubscriptionsPage() {
  const navigate = useNavigate();
  useBackButton(() => void navigate('/'));
  const { data, isLoading, error, refetch } = useSubscription();

  const items = data?.items ?? [];
  const active = items.filter((item) => item.has_access).length;

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <h1 className={styles.head}>Мои подписки</h1>

      {items.length > 0 ? (
        <>
          <GroupCaption>
            {active > 0 ? `Действуют · ${active}` : 'Нет действующих'}
          </GroupCaption>
          {items.map((item, index) => (
            <SubscriptionCard
              key={item.id}
              item={item}
              number={index + 1}
              onOpen={() => void navigate(`/subscriptions/${item.id}`)}
            />
          ))}
        </>
      ) : null}

      <Card>
        <Row
          icon="tag"
          title={items.length > 0 ? 'Добавить подписку' : 'Выбрать тариф'}
          subtitle="Ещё 3 устройства"
          onClick={() => void navigate('/plans')}
        />
        <Row
          icon="gift"
          title="Подарить другу"
          subtitle="Подписка в подарок по ссылке"
          onClick={() => void navigate('/gift')}
        />
      </Card>
    </Screen>
  );
}
